import * as React from "react";
import { Layout } from "antd5";
import { Redirect, Route, Switch, useLocation } from "wouter";

import ChangePassword from "components/account_management/ChangePassword";
import DisplaySettings from "components/account_management/DisplaySettings";
import { Integrations } from "components/account_management/Integrations";
import NotificationsPage from "components/account_management/notification_settings/NotificationsPage";
import BidSelfServe from "components/account_management/qualification-criteria/BidSelfServe";
import Subscription from "components/account_management/Subscription";
import UserManagement from "components/account_management/UserManagement";
import { withAppLayout } from "components/app_layout/AppLayout";
import { IntegrationSetup } from "components/integrations/IntegrationSetup";
import { assertCurrentUser } from "lib/currentUser";
import { DialogManagerProvider } from "lib/providers/DialogManager";
import * as tracking from "lib/tracking";
import { TrackingProvider } from "lib/tracking";
import { allowCrmIntegrationFlag } from "lib/utils/allowCrmIntegrationFlag";
import SignalSettings from "../../components/account_management/SignalSettings";
import SettingsNav from "../../components/app_layout/vertical_nav/SettingsNav";

import css from "./AccountManagementPage.module.scss";

// In the avatar navigation we want to just send the user to the generic /account-management
// The first tab in the list for now is account-settings/change-password but as we add more items this may change
// And in that case we'd modify the items below
const DEFAULT_ROUTE = "/account-management/subscription";

export enum Routes {
  FEED_SETTINGS = "/account-management/feed-settings",
  CHANGE_PASSWORD = "/account-management/change-password",
  SUBSCRIPTION = "/account-management/subscription",
  NOTIFICATIONS = "/account-management/notifications",
  MY_TEAM = "/account-management/my-team",
  DISPLAY_SETTINGS = "/account-management/display-settings",
  INTEGRATIONS_SETUP = "/account-management/integrations/setup",
  INTEGRATIONS = "/account-management/integrations",
  BID_SELF_SERVE = "/account-management/bid-self-serve",
}

type Props = {
  feed_settings?: {
    id: number;
  };
};

function AccountManagementPage() {
  const [location, setLocation] = useLocation();
  const [activeKey, setActiveKey] = React.useState<string>("account");
  const user = assertCurrentUser();
  const company = user.company;
  const trackingData = { "Context source": "Compare all plans page" };

  React.useEffect(() => {
    switch (location) {
      case Routes.FEED_SETTINGS:
        setActiveKey("feed-settings");
        tracking.pageView("Account Management - Feed Settings", {});
        break;
      case Routes.CHANGE_PASSWORD:
        setActiveKey("change-password");
        tracking.pageView("Account Management - Change Password", {});
        break;
      case Routes.DISPLAY_SETTINGS:
        setActiveKey("display-settings");
        tracking.pageView("Account Management - Display Settings", {});
        break;
      case Routes.SUBSCRIPTION:
        setActiveKey("subscription");
        tracking.pageView("Account Management - Subscription", {});
        break;
      case Routes.MY_TEAM:
        setActiveKey("my-team");
        tracking.pageView("Account Management - My team", {});
        break;
      case Routes.INTEGRATIONS:
        setActiveKey("integrations");
        tracking.pageView("Account Management - Integrations", {});
        break;
      case Routes.NOTIFICATIONS:
        setActiveKey("notifications");
        tracking.pageView("Account Management - Notifications", {});
        break;
      case Routes.BID_SELF_SERVE:
        setActiveKey("bid-self-serve");
        tracking.pageView("Account Management - Bid self serve", {});
        break;
    }
  }, [location, setLocation]);

  return (
    <Layout>
      <SettingsNav />
      <div className={activeKey === "integrations" ? css.whiteBackground : css.pageBackground}>
        <DialogManagerProvider>
          <Switch>
            <Route path={Routes.FEED_SETTINGS}>
              <TrackingProvider data={{ "Page source": "Feed settings Page" }}>
                <SignalSettings
                  internationalDataAccess={company.has_international_data_access ?? false}
                />
              </TrackingProvider>
            </Route>
            <Route path={Routes.DISPLAY_SETTINGS} component={DisplaySettings} />
            <Route path={Routes.CHANGE_PASSWORD} component={ChangePassword} />
            <Route path={Routes.SUBSCRIPTION}>
              <Subscription className={css.subscriptionPage} trackingData={trackingData} />
            </Route>
            <Route path={Routes.BID_SELF_SERVE} component={BidSelfServe} />
            <Route nest path={Routes.NOTIFICATIONS} component={NotificationsPage} />
            <Route path={Routes.MY_TEAM} component={UserManagement} />
            <Route path={Routes.INTEGRATIONS_SETUP} component={IntegrationSetup} />
            {allowCrmIntegrationFlag() ? (
              <Route path={Routes.INTEGRATIONS} component={Integrations} />
            ) : (
              <></>
            )}
            <Route>
              <Redirect href={DEFAULT_ROUTE} />
            </Route>
          </Switch>
        </DialogManagerProvider>
      </div>
    </Layout>
  );
}

function AdminBarComp({ feed_settings }: Props) {
  if (!feed_settings?.id) {
    return null;
  }

  return (
    <a href={`/admin/company/feed_settings/${feed_settings.id}`} target="_blank" rel="noreferrer">
      Go to Feed Settings AP
    </a>
  );
}

export default withAppLayout(AccountManagementPage, { AdminBarComp });
